// Component for user message input

import { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';

interface MessageInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function MessageInput({ 
  onSendMessage, 
  disabled = false,
  placeholder = "Your response..."
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [message]);

  const handleSubmit = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled) {
      onSendMessage(trimmedMessage);
      setMessage('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <motion.div
      style={{
        maxWidth: '42rem',
        margin: '0 auto',
        padding: '0 var(--space-xl)'
      }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.2 }}
    >
      <div style={{
        width: '100%',
        height: '1px',
        backgroundColor: 'var(--border-color)',
        marginBottom: 'var(--space-3xl)'
      }} />
      
      <div style={{ position: 'relative' }}>
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          maxLength={2000}
          style={{
            width: '100%',
            minHeight: '60px',
            maxHeight: '200px',
            padding: 'var(--space-lg)',
            backgroundColor: 'var(--color-surface)',
            border: '1px solid transparent',
            borderRadius: 'var(--border-radius)',
            fontSize: 'var(--text-base)',
            lineHeight: 'var(--leading-relaxed)',
            transition: 'border-color var(--transition-fast)',
            overflowY: 'auto',
            opacity: disabled ? 0.6 : 1,
            cursor: disabled ? 'not-allowed' : 'text'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-capacity-deep)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'transparent';
          }}
        />
        
        <div style={{
          position: 'absolute',
          bottom: 'var(--space-sm)',
          right: 'var(--space-lg)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-muted)',
          pointerEvents: 'none'
        }}>
          {disabled ? 'Thinking...' : 'Enter to send'}
        </div>
      </div>
    </motion.div>
  );
}
